import { Injectable } from "@angular/core";
import { HttpService } from "./utils/http.service";
import { IDocumentModel, IRmaClaimEmailModel, IRmaClaimModel, IRmaClaimResendEmailResultModel, IRmaClaimSettingsModel, IRmaClaimStatusHistoryModel, IRmaClaimSummaryModel, IRmaNewerOrdersModel, IRmaOrderValidationModel, IRmaSubmissionItemSearchModel } from "@models";

@Injectable()
export class RmaClaimsService {
    constructor(private httpService: HttpService) { }

    async validateOrder(orderId: string, invoiceNumber: string, jobNumber?: string, jobId?: number): Promise<IRmaOrderValidationModel> {
        return await this.httpService.get("/rmaClaims/validateOrder", { orderId, invoiceNumber, jobNumber: jobNumber || '', jobId: jobId || 0 });
    }

    async searchItems(searchText: string): Promise<IRmaSubmissionItemSearchModel[]> {
        return await this.httpService.get("/rmaClaims/searchItems", { searchText });
    }

    async getExistingClaimsForOrder(orderId: string, invoiceNumber: string): Promise<IRmaClaimSummaryModel[]> {
        return await this.httpService.get("/rmaClaims/getExistingClaimsForOrder", { orderId, invoiceNumber });
    }

    // Warranty claims a Latham warranty order can be linked to (not deleted/completed/in-review).
    async getWarrantyClaimsForLinking(): Promise<IRmaClaimSummaryModel[]> {
        return await this.httpService.get("/rmaClaims/getWarrantyClaimsForLinking");
    }

    async submitClaim(model: IRmaClaimModel, files: File[], captchaToken?: string): Promise<IRmaClaimModel> {
        const formData = new FormData();
        formData.append('model', JSON.stringify(model));
        if (captchaToken) {
            formData.append('captchaToken', captchaToken);
        }
        if (files) {
            for (const file of files) {
                formData.append('files', file, file.name);
            }
        }
        return await this.httpService.postMultipart("/rmaClaims/submitClaim", formData);
    }

    // Internal (authenticated) submission from the in-app New Submission dialog. The API stamps the
    // logged-in user as the submitter and auto-assigns the account exec when applicable.
    async submitClaimInternal(model: IRmaClaimModel, files: File[]): Promise<IRmaClaimModel> {
        const formData = new FormData();
        formData.append('model', JSON.stringify(model));
        if (files) {
            for (const file of files) {
                formData.append('files', file, file.name);
            }
        }
        return await this.httpService.postMultipart("/rmaClaims/submitClaimInternal", formData);
    }

    // Turns whatever submitClaim threw into something worth showing someone.
    //
    // An HttpErrorResponse for a request that never reached the api carries a ProgressEvent in .error,
    // and a page that renders err.error straight into a banner prints "[object ProgressEvent]". That
    // hides the only thing the person needs to know: the request died in transit rather than being
    // rejected, so the claim may well exist on the server and sending it again would duplicate it.
    describeSubmitError(err: any, fallback: string = "An error occurred while submitting the claim."): string {
        const diedInTransit = err?.status === 0
            || (typeof ProgressEvent !== "undefined" && err?.error instanceof ProgressEvent);

        if (diedInTransit)
            return "The connection dropped before the claim came back. It may still have been submitted, so check the claims list before sending it again.";

        if (typeof err?.error === "string" && err.error)
            return err.error;

        if (typeof err?.error?.message === "string" && err.error.message)
            return err.error.message;

        return err?.message || fallback;
    }

    async getAllClaims(): Promise<IRmaClaimModel[]> {
        return await this.httpService.get("/rmaClaims/getAllClaims");
    }

    async createPendingFromJob(jobId: number): Promise<IRmaClaimModel> {
        return await this.httpService.post(`/rmaClaims/createPendingFromJob?jobId=${jobId}`, {});
    }

    // Internal submission only: orders placed for the same homeowner after this one (matched by address,
    // then homeowner), so the submitter can move the claim to the most recent order.
    async getNewerOrdersForClaim(lathamOrderId: number): Promise<IRmaNewerOrdersModel> {
        return await this.httpService.get("/rmaClaims/getNewerOrdersForClaim", { lathamOrderId });
    }

    // Warranty-coordinator-only: create a claim for a Latham order that isn't in Skedit. At least one
    // photo is required; they're filed to the claim's Files tab.
    async createManualClaim(model: IRmaClaimModel, photos: File[]): Promise<IRmaClaimModel> {
        const formData = new FormData();
        formData.append('model', JSON.stringify(model));
        for (const photo of photos || [])
            formData.append('files', photo, photo.name);
        return await this.httpService.postMultipart("/rmaClaims/createManualClaim", formData);
    }

    async getClaimsForGridView(request: { page: number, recordsPerPage: number, additionalParams: { searchText?: string, claimType?: string, status?: string, accountExecutiveUserId?: string, followerUserIds?: string, startDate?: string, endDate?: string, staleOnly?: string, closedFilter?: string } }): Promise<{ totalRecords: number, data: IRmaClaimModel[] }> {
        return await this.httpService.post("/rmaClaims/getClaimsForGridView", request);
    }

    async getClaim(rmaClaimId: number): Promise<IRmaClaimModel> {
        return await this.httpService.get("/rmaClaims/getClaim", { rmaClaimId });
    }

    async updateClaim(model: IRmaClaimModel): Promise<IRmaClaimModel> {
        return await this.httpService.post("/rmaClaims/updateClaim", model);
    }

    async updateClaimStatus(rmaClaimId: number, status: string): Promise<void> {
        return await this.httpService.post("/rmaClaims/updateClaimStatus", { rmaClaimId, status });
    }

    async closeClaim(rmaClaimId: number): Promise<void> {
        return await this.httpService.post(`/rmaClaims/closeClaim?rmaClaimId=${rmaClaimId}`, {});
    }

    async reopenClaim(rmaClaimId: number): Promise<void> {
        return await this.httpService.post(`/rmaClaims/reopenClaim?rmaClaimId=${rmaClaimId}`, {});
    }

    async deleteClaim(rmaClaimId: number): Promise<void> {
        return await this.httpService.post(`/rmaClaims/deleteClaim?rmaClaimId=${rmaClaimId}`, {});
    }

    async restoreClaim(rmaClaimId: number): Promise<void> {
        return await this.httpService.post(`/rmaClaims/restoreClaim?rmaClaimId=${rmaClaimId}`, {});
    }

    async getClaimsByStatus(filters?: { accountExecutiveUserId?: number, followerUserIds?: number[] }): Promise<IRmaClaimModel[]> {
        const params: any = {};
        if (filters) {
            if (filters.accountExecutiveUserId != null)
                params.accountExecutiveUserId = filters.accountExecutiveUserId;
            if (filters.followerUserIds && filters.followerUserIds.length)
                params.followerUserIds = filters.followerUserIds;
        }
        return await this.httpService.get("/rmaClaims/getClaimsByStatus", params);
    }

    async getClaimDocuments(rmaClaimId: number): Promise<IDocumentModel[]> {
        return await this.httpService.get("/rmaClaims/getClaimDocuments", { rmaClaimId });
    }

    async addClaimDocuments(rmaClaimId: number, files: File[]): Promise<IDocumentModel[]> {
        const formData = new FormData();
        if (files) {
            for (const file of files)
                formData.append('files', file, file.name);
        }
        return await this.httpService.postMultipart(`/rmaClaims/addClaimDocuments?rmaClaimId=${rmaClaimId}`, formData);
    }

    async getClaimEmails(rmaClaimId: number): Promise<IRmaClaimEmailModel[]> {
        return await this.httpService.get("/rmaClaims/getClaimEmails", { rmaClaimId });
    }

    async addClaimEmails(rmaClaimId: number, files: File[]): Promise<IRmaClaimEmailModel[]> {
        const formData = new FormData();
        if (files) {
            for (const file of files)
                formData.append('files', file, file.name);
        }
        return await this.httpService.postMultipart(`/rmaClaims/addClaimEmails?rmaClaimId=${rmaClaimId}`, formData);
    }

    async getEmailAttachment(rmaClaimId: number, documentId: number, index: number): Promise<Blob> {
        return await this.httpService.getFileBlob("/rmaClaims/getEmailAttachment", { rmaClaimId, documentId, index });
    }

    async getSettings(): Promise<IRmaClaimSettingsModel> {
        return await this.httpService.get("/rmaClaims/getSettings");
    }

    async updateSettings(model: IRmaClaimSettingsModel): Promise<IRmaClaimSettingsModel> {
        return await this.httpService.post("/rmaClaims/updateSettings", model);
    }

    async resendConfirmationEmail(rmaClaimId: number): Promise<IRmaClaimResendEmailResultModel> {
        return await this.httpService.post(`/rmaClaims/resendConfirmationEmail?rmaClaimId=${rmaClaimId}`, {});
    }

    async sendJobToBilling(rmaClaimId: number): Promise<void> {
        return await this.httpService.post(`/rmaClaims/sendJobToBilling?rmaClaimId=${rmaClaimId}`, {});
    }

    async getClaimsForRelated(opts: { jobId?: number, invoiceId?: number }): Promise<IRmaClaimModel[]> {
        const params: any = {};
        if (opts.jobId != null) params.jobId = opts.jobId;
        if (opts.invoiceId != null) params.invoiceId = opts.invoiceId;
        return await this.httpService.get("/rmaClaims/getClaimsForRelated", params);
    }

    async getStatusHistory(rmaClaimId: number): Promise<IRmaClaimStatusHistoryModel[]> {
        return await this.httpService.get("/rmaClaims/getStatusHistory", { rmaClaimId });
    }

    async exportClaimsToCSV(filters: {
        searchText?: string,
        claimType?: string,
        status?: string,
        accountExecutiveUserId?: string,
        followerUserIds?: string,
        startDate?: string,
        endDate?: string,
        staleOnly?: string,
        myClaimsOnly?: string,
        closedFilter?: string
    }): Promise<Blob> {
        return await this.httpService.getBlob("/rmaClaims/exportClaimsToCSV", {
            searchText: filters.searchText || '',
            claimType: filters.claimType || '',
            status: filters.status || '',
            accountExecutiveUserId: filters.accountExecutiveUserId || '',
            followerUserIds: filters.followerUserIds || '',
            startDate: filters.startDate || '',
            endDate: filters.endDate || '',
            staleOnly: filters.staleOnly || 'false',
            myClaimsOnly: filters.myClaimsOnly || 'false',
            closedFilter: filters.closedFilter || 'open'
        });
    }
}
